import React, { useState } from 'react';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';
import { Form, getFormValues, reduxForm } from 'redux-form';
import { Box, List, makeStyles, Typography } from '@material-ui/core';

import {
  CustomIcon,
  Dropzone,
  FilePreview,
  ModalLayout,
  Select,
  TextField,
  ThemeButton,
  Translation
} from '../../common';
import { connectTo } from '../../../utils/';
import { DOC, DOCX, JPEG, PDF, PNG } from '../../../constants/file-upload';
import { useAppWidthClass } from '../../../utils/hooks';
import { enqueueSnackbar } from '../../../actions/snackbar';

const SELECT_VALUES = [
  'VOUCHER',
  'CONFIRMATION_OF_TERMINATION',
  'CERTIFICATE_OF_REGISTRATION',
  'COPY_OF_INVOICE',
  'DEATH_CERTIFICATE',
  'OTHER'
].map(value => ({
  value,
  label: `UPLOAD_FILE_TYPE_${value}`,
  id: `page.post-box.component.upload-select.${value}-item`
}));

const ACCEPTED_TYPES = [PDF, DOC, DOCX, JPEG, PNG];
const MAX_SIZE = 100 * 1024 * 1024; // 100 Mb
const MAX_FILE_COUNT = 10;

const filesCheck = files => !!files && !!files.length;
const fileValidator = files =>
  filesCheck(files) ? undefined : 'Files are required';

const typeCheck = type => !!type;
const typeValidator = type =>
  typeCheck(type) ? undefined : 'Type is required';

const useStyles = makeStyles(theme => ({
  content: {
    marginTop: 32,
    display: 'flex',
    flexDirection: 'row'
  },
  contentCompact: {
    flexDirection: 'column',
    width: '100%',
    maxWidth: 320,
    margin: '32px auto 0 auto'
  },
  dropzone: {
    flexShrink: 0,
    width: 264,
    height: 264,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    border: `4px dashed ${theme.palette.text.disabled}`,
    boxSizing: 'border-box',
    borderRadius: 16,
    transition: theme.transitions.custom.fast
  },
  dropzoneActive: {
    borderColor: theme.palette.primary.main
  },
  hasFiles: {
    height: 144
  },
  dropzoneCompact: {
    width: '100%',
    height: 144
  },
  uploadExplanation: {
    color: theme.palette.text.disabled,
    transition: theme.transitions.custom.fast
  },
  uploadExplanationActive: {
    color: theme.palette.primary.main
  },
  addIcon: {
    width: 20,
    height: 20,
    marginTop: 18
  },
  fileList: {
    maxWidth: 264,
    padding: 0,
    marginTop: 28
  },
  fileListCompact: {
    maxWidth: 'unset',
    padding: 0,
    marginTop: 20
  },
  fileListOnly: {
    marginTop: 0
  },
  file: {
    padding: 0,

    '&:nth-of-type(1n+2)': {
      marginTop: 8
    }
  },
  details: {
    flexGrow: 1,
    marginLeft: 24,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  },
  detailsCompact: {
    marginLeft: 0,
    marginTop: 24
  },
  typeTitle: {
    color: theme.palette.text.secondary
  },
  select: {
    '@supports (-webkit-hyphens:none)': {
      paddingTop: 7,
      paddingBottom: 6
    }
  },
  typeSelect: {
    marginTop: 8,
    width: '100%'
  },
  typeSelectOption: {
    '&:hover': {
      color: theme.palette.primary.contrastText
    }
  },
  descriptionTitle: {
    marginTop: 16,
    color: theme.palette.text.secondary
  },
  description: {
    width: '100%'
  },
  descriptionMargin: {
    marginTop: 8
  },
  submit: {
    margin: '32px auto 0 auto'
  }
}));

const UploadFileModalContent = ({
  onClose,
  handleSubmit,
  values,
  invalid,
  change,
  isFileLoading,
  translations
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { isMobileWidth } = useAppWidthClass();
  const [isDragActive, setDragActive] = useState(false);

  const hasFiles = values && values.files && values.files.length > 0;
  const showDropZone = !hasFiles || values.files.length < MAX_FILE_COUNT;

  const onFileRemove = file => () => {
    if (hasFiles) {
      const filesCopy = values.files.slice();
      const fileIdx = filesCopy.indexOf(file);
      filesCopy.splice(fileIdx, 1);
      change('files', filesCopy);
    }
  };

  const onDropzoneFileExcess = fileNames => {
    dispatch(
      enqueueSnackbar({
        message: `Some files are excessing max file size: ${fileNames.join(
          ', '
        )}! They were filtered.`,
        options: {
          variant: 'error'
        }
      })
    );
  };

  return (
    <ModalLayout
      icon="uploadIcon"
      title="UPLOAD_FILE_DIALOG_TITLE"
      onClose={onClose}
      closeButtonId="postbox.upload.close"
    >
      <Form
        className={classNames(
          classes.content,
          isMobileWidth && classes.contentCompact
        )}
        onSubmit={handleSubmit}
      >
        <Box>
          {showDropZone && (
            <Dropzone
              multiple
              multipleAdding
              name="files"
              maxFileCount={MAX_FILE_COUNT}
              maxSize={MAX_SIZE}
              accept={ACCEPTED_TYPES}
              className={classNames(
                classes.dropzone,
                isDragActive && classes.dropzoneActive,
                hasFiles && classes.hasFiles,
                isMobileWidth && classes.dropzoneCompact
              )}
              id="page.postbox.component.upload-file-modal.file-dropzone"
              validate={fileValidator}
              onMaxSizeExcess={onDropzoneFileExcess}
              setDragActive={setDragActive}
            >
              <Typography
                className={classNames(
                  classes.uploadExplanation,
                  isDragActive && classes.uploadExplanationActive
                )}
                component="h4"
                variant="h4"
              >
                <Translation inline>UPLOAD_EXPLANATION</Translation>
              </Typography>
              <CustomIcon
                className={classes.addIcon}
                icon="addIcon"
                size="custom"
                variant={isDragActive ? 'primary' : 'disabled'}
              />
            </Dropzone>
          )}
          {hasFiles && (
            <List
              className={classNames(
                classes.fileList,
                isMobileWidth && classes.fileListCompact,
                !showDropZone && classes.fileListOnly
              )}
            >
              {values.files.map((file, index) => (
                <FilePreview
                  key={index}
                  className={classes.file}
                  file={file}
                  removeButtonId={`page.postbox.component.file-upload-modal.file-remove-button${
                    index > 0 ? `.${index}` : ''
                  }`}
                  onRemove={onFileRemove(file)}
                />
              ))}
            </List>
          )}
        </Box>
        <Box
          className={classNames(
            classes.details,
            isMobileWidth && classes.detailsCompact
          )}
        >
          <Typography
            className={classes.typeTitle}
            component="h3"
            variant="body2"
          >
            <Translation inline>UPLOAD_FILE_TITLE</Translation>
          </Typography>
          <Select
            name="type"
            validate={typeValidator}
            classNameRoot={classes.select}
            className={classes.typeSelect}
            classNameMenuItem={classes.typeSelectOption}
            options={SELECT_VALUES}
            translated
          />
          <Typography
            className={classes.descriptionTitle}
            component="h3"
            variant="body2"
          >
            <Translation inline>UPLOAD_FILE_DESCIPTION</Translation>
          </Typography>
          <TextField
            name="description"
            className={classes.description}
            classNameRoot={classes.descriptionMargin}
            placeholder={
              translations && translations['UPLOAD_FILE_DESCIPTION_PLACEHOLDER']
            }
            multiline
            rows={5}
          />
          <ThemeButton
            className={classes.submit}
            id="page.postbox.component.upload-file-modal.upload-file-button"
            variant="contained"
            color="primary"
            type="submit"
            textKey="UPLOAD_BUTTON"
            disabled={invalid || isFileLoading}
          />
        </Box>
      </Form>
    </ModalLayout>
  );
};

const UPLOAD_FILES_FORM = 'upload-files';

const connected = connectTo(
  state => ({
    values: getFormValues(UPLOAD_FILES_FORM)(state)
  }),
  {},
  UploadFileModalContent
);

export default reduxForm({
  form: UPLOAD_FILES_FORM
})(connected);
